import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";
import ContratoAutocomplete from "../../../components/autocomplete/ContratoAutocomplete";
import TarjetasAutocomplete from "../../../components/autocomplete/TarjetasAutocomplete";
import EstadoSelect from "../../../components/select/EstadoSelect";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson, notify } from "../../../utils/http";

export default function CrearContratoTarjeta({
  onClose = () => {},
  onCreated = () => {},
  embedded = false,
  contratoPredefinido = null,
  initialData = null,
}) {
  // Estados
  const [contratoObj, setContratoObj] = useState(
    initialData
      ? { id: initialData.contratoId, codigo: initialData.contratoCodigo }
      : (contratoPredefinido || null)
  );
  const [tarjetaObj, setTarjetaObj] = useState(
    initialData
      ? { id: initialData.tarjetaId, numero: initialData.tarjetaNumero }
      : null
  );
  const [estadoObj, setEstadoObj] = useState(
    initialData
      ? { id: initialData.estadoId, label: initialData.estadoNombre }
      : null
  );
  const [existingId, setExistingId] = useState(initialData?.id || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isValid = contratoObj && tarjetaObj && estadoObj;

  useEffect(() => {
    if (embedded) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, embedded]);

  useEffect(() => {
    if (initialData) {
      setContratoObj({ id: initialData.contratoId, codigo: initialData.contratoCodigo });
      setTarjetaObj({ id: initialData.tarjetaId, numero: initialData.tarjetaNumero });
      setEstadoObj({ id: initialData.estadoId, label: initialData.estadoNombre });
      setExistingId(initialData.id);
    } else {
      setContratoObj(contratoPredefinido || null);
      setTarjetaObj(null);
      setEstadoObj(null);
      setExistingId(null);
    }
  }, [initialData, contratoPredefinido]);

  const handleSave = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    setError("");

    const payload = {
      contratoId: contratoObj.id,
      tarjetaId: tarjetaObj.id,
      estadoId: estadoObj.id,
    };

    try {
      let result;
      if (existingId) {
        const url = apiRoutes.contratoTarjeta.editarUrl(existingId);
        result = await appFetchJson(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        notify({ type: "success", message: "Asignación actualizada correctamente" });
      } else {
        result = await appFetchJson(apiRoutes.contratoTarjeta.agregarUrl(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        notify({ type: "success", message: "Asignación creada correctamente" });
        setExistingId(result.id);
      }
      onCreated(result);
      if (!embedded) onClose();
      else {
        // Limpiar solo si es creación y no hay contrato predefinido
        if (!existingId && !contratoPredefinido) {
          setContratoObj(null);
          setTarjetaObj(null);
          setEstadoObj(null);
          setExistingId(null);
        }
      }
    } catch (e) {
      console.error("Error guardando asignación:", e);
      // El mensaje de error se extrae de e.message, el cual debe ser proporcionado por appFetchJson
      // basándose en la respuesta del backend (ej: { error: "Contrato no encontrado" })
      const errorMsg = e?.message || "Error al guardar la asignación.";
      setError(errorMsg);
      notify({ type: "error", message: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <>
      {!embedded && (
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">Asignar Contrato a Tarjeta</h2>
          <button className="modal-close-x" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
      )}

      <div className={embedded ? "" : "modal-body"}>
        {!embedded && (
          <p className="text-gray-600 mb-4 italic">
            Complete los datos para asignar un contrato a una tarjeta.
          </p>
        )}

        <div className="form-grid">
          <div className="form-row col-span-2">
            <label className="form-label">Contrato *</label>
            <input
              type="text"
              className="input w-full bg-gray-100 cursor-not-allowed"
              value={contratoObj?.codigo || ""}
              disabled
              readOnly
            />
          </div>

          <div className="form-row col-span-2">
            <label className="form-label">Tarjeta *</label>
            <div className="w-full">
              <TarjetasAutocomplete
                value={tarjetaObj}
                onChange={setTarjetaObj}
                placeholder="Buscar por número..."
              />
            </div>
          </div>

          <div className="form-row col-span-2">
            <label className="form-label">Estado *</label>
            <EstadoSelect
              tipoId={20}
              value={estadoObj}
              onChange={setEstadoObj}
              autoLoad={true}
            />
          </div>
        </div>

        {error && <div className="text-red-600 mt-3 font-medium">{error}</div>}
      </div>

      <div className={embedded ? "mt-6 border-b pb-6" : "modal-footer"}>
        <div className="btn-row">
          <BtnGuardar
            label={saving ? "Guardando..." : embedded ? "Guardar y continuar" : "Guardar"}
            isValid={isValid}
            saving={saving}
            onClick={handleSave}
          />
          {!embedded && <BtnCancelar onClick={onClose} />}
        </div>
      </div>
    </>
  );

  if (embedded) {
    return <div className="bg-white p-1">{content}</div>;
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        {content}
      </div>
    </div>
  );
}